<template>
  <div
    :id="isProductCard ? null : `product-form`"
    class="product-form"
    :class="{
      [$class('loading')]: loading,
      'product-form--personalisation': isPersonalisationProduct,
      'product-form--unavailable': !isAvailableToPurchase,
      'product-form--is-preorder-closed': isPreOrder && isPreOrderClosing,
    }"
    data-sign-post-location="main"
    js-sign-post
  >
    <btn
      v-if="showQuickAddRedirectButton"
      class="product-form__add-to-cart"
      :class="{
        'BIS_trigger': showBackInStockButton
      }"
      custom-element="a"
      :href="product.url"
      :label="quickAddRedirectText"
      :modifiers="addToCartModifiers"
      :data-product-data="showBackInStockButton ? JSON.stringify(product) : null"
    />

    <btn
      v-else-if="alreadyPurchasedMembership"
      class="product-form__add-to-cart product-form__add-to-cart--membership-purchased"
      :label="$string('product.cta.collectors_view_members_area')"
      :modifiers="addToCartModifiers"
      :url="$variable('collectorsClub.membersPage')"
    />

    <form
      v-else
      :id="`product-form-template--${product.id}__main`"
      class="product-form__form"
      method="post"
      action="/cart/add"
      data-type="add-to-cart-form"
      @submit.prevent="handleAddToCart"
    >
      <product-options
        v-show="showProductOptions"
        class="product-form__options"
        :loading="loading"
        :product="product"
        :section-id="sectionId"
        :settings="localSettings"
        :variant="variant"
        @update-variant="handleUpdateVariant"
      />

      <product-personalisation-form
        v-if="isPersonalisationProduct"
        class="product-form__personalisation"
        :loading="loading"
        :personalisation-guides="personalisationGuides"
        :product="product"
        :section-id="sectionId"
        :variant="variant"
        @update-personalisation="handleUpdatePersonalisation"
        @update-personalisation-error="(isError) => personalisationError = isError"
      />

      <recipient-form
        v-if="getComponentVisible('recipient-form') && product.giftCard"
        ref="recipientForm"
        class="product-form__recipient-form"
        :loading="loading"
        :section-id="sectionId"
        @update-line-item-properties="handleUpdateLineItemProperties"
      />

      <div
        v-if="!getComponentActive('recipient-form') && product.giftCard"
        class="recipient-form"
      >
        <checkbox-input
          :id="`gift-card-recipient-show-${sectionId}`"
          v-model="checkboxPlaceholder"
          class="recipient-form__checkbox"
          disabled
          :label="$string('product.form.send_as_gift')"
        />
      </div>

      <div
        v-if="personalisedWithoutText &&
          ($variable('personalisation.disclaimer1') ||
            $variable('personalisation.disclaimer2'))"
        class="product-form__disclaimer-container"
      >
        <p
          v-if="$variable('personalisation.disclaimer1')"
          class="product-form__disclaimer"
        >
          <icon-warning />
          <span
            class="text-body-xs-regular"
            v-html="$variable('personalisation.disclaimer1')"
          />
        </p>
        <p
          v-if="$variable('personalisation.disclaimer2')"
          class="personalisation-form__disclaimer"
        >
          <icon-warning />
          <span
            class="text-body-xs-regular"
            v-html="$variable('personalisation.disclaimer2')"
          />
        </p>
      </div>

      <div class="product-form__actions">
        <div
          v-if="localSettings.displayQuantitySelector"
          v-show="isAvailableToPurchase"
          class="product-form__quantity-selector-wrapper"
        >
          <div
            class="product-form__quantity-label text-body-xs-regular"
            aria-hidden="true"
            v-text="$string('product.form.quantity_label')"
          />

          <quantity-selector
            class="product-form__quantity-selector"
            :min="1"
            :quantity="quantity"
            :state="loading ? 'loading' : 'ready'"
            :max="onePerOrderEnabled ? maxProductLimit : false"
            @update-quantity="handleUpdateQuantity"
          />
        </div>

        <input
          v-else
          name="quantity"
          readonly
          type="hidden"
          :value="quantity"
        >

        <btn
          v-if="isProductCard && limitPerProductReached"
          class="product-form__add-to-cart"
          :label="$string('product.cta.view_product')"
          :modifiers="addToCartModifiers"
          custom-element="a"
          :href="product.url"
          type="button"
        />

        <btn
          v-else
          v-show="!showBackInStockButton && !alreadyPurchasedMembership"
          class="product-form__add-to-cart"
          :label="addToCartText"
          :modifiers="addToCartModifiers"
          :show-state-icon="state === 'adding'"
          type="submit"
          :disabled="
            isCollectorsClubRestricted ||
              personalisationError ||
              limitPerProductReached ||
              isPreOrder && !variant?.available ||
              isPreOrder && isPreOrderClosing ||
              hasPreOrderTag && isPreOrderLaunch ||
              !hasPreOrderTag && hasAvailableToBuyDate
          "
        >
          <template #stateIcon>
            <icon-loading />
          </template>
        </btn>

        <btn
          v-show="showBackInStockButton"
          id="BIS_trigger"
          class="product-form__back-in-stock"
          :label="$string('product.cta.back_in_stock_notify_me')"
          type="button"
        />

        <wishlist-button
          v-if="!isProductCard"
          class="product-form__wishlist"
          :product="product"
          template="form"
          :variant="variant ? variant : product.variants[0]"
        />
      </div>
    </form>

    <notification-panel
      v-if="error"
      class-container="product-form__error"
      :show-close="false"
      :text="error"
      type="error"
    />

    <p
      v-if="limitPerProductReached"
      class="product-form__one-per-order-alert text-body-s-regular"
      v-text="$string(
        'product.form.one_per_order_alert',
        {
          replace: { max_quantity: product?.limits?.perCustomer },
        },
      )"
    />

    <div
      v-if="isCollectorsClubRestricted && $variable('settings.collectorsClub.pdpLoginText')"
      class="product-form__collectors-login-message"
    >
      <icon-info />

      <div
        class="product-form__collectors-login-text text-body-xs-regular"
        v-html="$variable('settings.collectorsClub.pdpLoginText')"
      />
    </div>

    <klarna-messaging
      v-if="!isCollectorsClubRestricted && !alreadyPurchasedMembership && !isProductCard"
      namespace="product-form"
      :price="variant?.price?.amount"
      :variant="variant"
    />
  </div>
</template>

<script>

/**
 * Vue: Product form (product-form)
 * -----------------------------------------------------------------------------
 * Product form to handle variant selection and adding to cart.
 *
 * @param {String} [addToCartModifiers] - Custom button style.
 * @param {Boolean} [isProductCard] - Display in Product Card.
 * @param {Boolean} [loading] - Loading state.
 * @param {Array} [personalisationGuides] - Data for personalisation guide
 * images.
 * @param {Boolean|Object} product - Canvas formatted product object.
 * @param {String} sectionId - Section ID.
 * @param {Object} settings - Product form settings.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 * @param {Boolean} [onePerOrderEnabled] - one per order enabled.
 *
 * @emits updateVariant - Emit new variant object.
 *
 */
/* eslint-disable no-underscore-dangle */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { handleDate } from '~/helpers/general'

import Btn from '~global/btn/btn'
import CheckboxInput from '~async/utils/checkbox-input/checkbox-input'
import KlarnaMessaging from '~async/klarna-messaging/klarna-messaging'
import NotificationPanel from '~async/utils/notification-panel/notification-panel'
import ProductOptions from '~async/product-form/product-options'
import ProductPersonalisationForm from '~async/product-personalisation-form/product-personalisation-form'
import QuantitySelector from '~async/quantity-selector/quantity-selector'
import WishlistButton from '~async/wishlist-button/wishlist-button'

import IconInfo from '~icons/general/info.svg'
import IconLoading from '~icons/general/loading.svg'
import IconWarning from '~icons/general/warning.svg'

import { emitUpdateVariant } from '~/helpers/vue'

export default {
  name: 'ProductForm',

  components: {
    Btn,
    CheckboxInput,
    IconInfo,
    IconLoading,
    IconWarning,
    WishlistButton,
    KlarnaMessaging,
    NotificationPanel,
    ProductOptions,
    ProductPersonalisationForm,
    QuantitySelector,
    RecipientForm: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.recipient-form' */
        '~async/product-form/recipient-form'
      ),
    }),
  },

  props: {
    alreadyPurchasedMembership: {
      type: Boolean,
      default: false,
    },
    addToCartModifiers: {
      type: String,
      default: 'primary block center',
    },
    isProductCard: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    personalisationGuides: {
      type: Array,
      default: () => [],
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    sectionId: {
      type: String,
      default: '',
      required: true,
    },
    settings: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    variant: {
      type: [Boolean, null, Object],
      default: false,
      required: true,
    },
    onePerOrderEnabled: {
      type: Boolean,
      default: false,
    },
    initialQuantity: {
      type: Number,
      default: 1,
    },
  },

  emits: {
    'update-variant': emitUpdateVariant,
    'update-quantity': (quantity) => typeof quantity === 'number',
    'update-personalising': (value) => typeof value === 'boolean',
  },

  data() {
    return {
      backInStockAppActive: false,
      checkboxPlaceholder: false,
      defaultSettings: {
        autoOpenCartDrawer: true,
        displayQuantitySelector: true,
        displaySingleOptionNames: true,
        displaySingleValueOptions: false,
        displaySingleVariantOption: false,
        dropdownOptions: [],
        excludedInitialSelectOptions: ['length', 'size', 'waist', 'width'],
        hideGiftCardOptionNames: true,
        hideInvalidValues: false,
        initialSelect: true,
        resetFormOnAdd: false,
        resetRecipientFormOnAdd: true,
        selectDisabledValues: true,
        showErrorMessages: false,
      },
      error: '',
      personalisationError: false,
      lineItemProperties: {},
      quantity: this.initialQuantity,
      state: 'ready',
      timeout: {},
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
      getCollectorsClubRestricted: 'collectorsClub/getProductCollectorsClubRestricted',
    }),

    ...mapState({
      cart: (state) => state.cart.response,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Checks if the product is personalised but does not require text input.
     * Returns true if personalisation is enabled without text, else false.
    */
    personalisedWithoutText() {
      return Boolean(this.product?.personalisation?.enabledWithoutText)
    },

    /**
     * Checks if the product supports personalisation on both sides.
     * Returns true if enabled, else false.
    */
    personalisedBothSide() {
      return Boolean(this.product?.personalisation?.personalisedBothSide)
    },

    /**
     * Checks if the product has the 'PERSONALISATION: BOTH' tag.
     * Used to determine if both-side personalisation is enabled via tags.
     * @returns {Boolean}
    */
    personalisedBothSideTag() {
      const tags = this.product?.tags || []
      return Boolean(tags.includes('PERSONALISATION: BOTH'))
    },

    /**
     * Compute restricted products for collectors club
     * @returns {Boolean}
     */
    isCollectorsClubRestricted() {
      const isCollectorsClubProduct = Boolean(this.product?.limits?.collectors) ||
        Boolean(this.product?.limits?.collectorsEarly)

      const collectorsClubRestricted = isCollectorsClubProduct &&
        !window.cnvs.customer.isCollector

      return this.getCollectorsClubRestricted || collectorsClubRestricted
    },

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (this.isCollectorsClubRestricted) {
        return this.$string('product.cta.collectors_only')
      }

      if (this.isPersonalisationProduct) {
        // Check Coming Soon for personalized products
        if (this.isPreOrder && this.isPreOrderLaunch) {
          return this.$string('product.cta.coming_soon')
        }

        if (!this.hasPreOrderTag && this.hasAvailableToBuyDate) {
          return this.$string('product.cta.coming_soon')
        }

        /**
         * Ignore selected variant and fallback to the cheapest variant instead
         * if no variant is currently selected.
         */
        const cheapestVariant = [...this.product.variants].sort((variantA, variantB) => {
          return variantA.price.amount - variantB.price.amount
        })[0]

        const variantObject = this.variant || cheapestVariant
        const variantPrice = variantObject?.price.amount
          ? `${this.$formatMoney(variantObject.price.amount)} | `
          : ''

        return this.state === 'added'
          ? this.$string('product.cta.added_to_cart')
          : `${variantPrice}${this.$string('product.personalisation.add_to_cart')}`
      }

      if (!this.variant) {
        return this.$string('product.cta.select_variant')
      }

      if (this.isPreOrder && this.isPreOrderLaunch) {
        return this.$string('product.cta.coming_soon')
      }

      if (this.isPreOrder && this.isPreOrderClosing) {
        return this.$string('product.cta.pre_order_closing')
      }

      if (this.isPreOrder && !this.variant?.available) {
        return this.$string('product.cta.pre_order_sold_out')
      }

      if (this.isPreOrder) {
        return this.$string('product.cta.pre_order_available')
      }

      if (!this.hasPreOrderTag && this.hasAvailableToBuyDate) {
        return this.$string('product.cta.coming_soon')
      }

      if (!this.variant?.available) {
        return this.$string('product.cta.sold_out')
      }

      return this.state === 'added'
        ? this.$string('product.cta.added_to_cart')
        : this.$string('product.cta.add_to_cart')
    },

    /**
     * Compute quick add redirect text.
     * @returns {String}
     */
    quickAddRedirectText() {
      if (this.isBookingProduct) {
        return this.$string('product.cta.book')
      }

      if (this.isPersonalisationProduct || this.personalisedBothSideTag) {
        // Check Coming Soon for personalized products
        if (this.isPreOrder && this.isPreOrderLaunch) {
          return this.$string('product.cta.coming_soon')
        }

        if (!this.hasPreOrderTag && this.hasAvailableToBuyDate) {
          return this.$string('product.cta.coming_soon')
        }

        return this.$string('product.cta.personalise')
      }

      if (this.showBackInStockButton) {
        return this.$string('product.cta.notify')
      }

      if (!this.isPersonalisationProduct && this.product?.price.varies) {
        return this.$string('product.cta.select_option')
      }

      return this.$string('product.cta.add_to_cart')
    },

    /**
     * Compute local settings.
     * @returns {Object}
     */
    localSettings() {
      return {
        ...this.defaultSettings,
        ...this.settings,
      }
    },

    /**
     * Compute if the variant is available.
     * - Defaults to true is variant is falsey.
     * @returns {Boolean}
     */
    variantAvailable() {
      return this.variant ? this.variant?.available : true
    },

    /**
     * Compute whether to show the Back In Stock button.
     * - Checks that the BIS app is active, that the variant is unavailable and
     *   check if the BIS app hide for product tags setting matches the
     *   product's tags.
     * @returns {Boolean}
     */
    showBackInStockButton() {
      const backInStockHiddenProductTagsSetting = window.BIS?.Config?.hide_for_product_tags

      const backInStockHiddenTags = backInStockHiddenProductTagsSetting ? backInStockHiddenProductTagsSetting.split(', ') : []

      const hideBackInStockByTags = this.product?.tags?.some((tag) => {
        return backInStockHiddenTags.includes(tag.toLowerCase())
      })

      // Hide BIS for Coming Soon products (both pre-order and regular)
      if (this.isPreOrder && this.isPreOrderLaunch) {
        return false
      }

      if (!this.hasPreOrderTag && this.hasAvailableToBuyDate) {
        return false
      }

      if (this.isPreOrder && !this.variant?.available) {
        return false
      }

      return this.backInStockAppActive && !this.variantAvailable && !hideBackInStockByTags
    },

    /**
     * Compute show product form element.
     * @returns {Boolean}
     */
    showProductOptions() {
      if (this.loading && !this.product) {
        return true
      }

      const multipleOptions = this.product?.options?.some((option) => {
        return option.values.length > 1
      })

      /**
       * If multiple variants then ensure they have more than one option.
       */
      const showOptionsMultiVariant =
        !this.product.hasOnlyDefaultVariant &&
        multipleOptions

      /**
       * If single variant then check if settings enable display.
       */
      const showOptionsSingleVariant =
        this.localSettings.displaySingleValueOptions &&
        this.localSettings.displaySingleVariantOption

      return showOptionsMultiVariant || showOptionsSingleVariant
    },

    /**
     * Compute if the current product is a personalisation product.
     * @returns {Boolean}
     */
    isPersonalisationProduct() {
      if (this.product?.personalisation) {
        return Boolean(this.product?.personalisation?.enabled)
      }

      const isEnabled = this.product?.tags?.some((tag) => {
        return ['MARK_IF_PERSONALISED: Yes', 'NATURE: Personalised Pottery'].includes(tag)
      })

      return isEnabled
    },

    /**
     * Compute if the current product is a booking product.
     * @returns {Boolean}
     */
    isBookingProduct() {
      return this.product?.metafields?.bookthatapp?.length
    },

    /**
     * Compute if variant is available to purchase.
     * - Must be in stock and not collectors club restricted
     * @returns {Boolean}
     */
    isAvailableToPurchase() {
      return this.variantAvailable && !this.isCollectorsClubRestricted
    },

    /**
     * Compute limit per product reached.
     * @returns {Boolean}
     */
    limitPerProductReached() {
      return this.onePerOrderEnabled && this.maxProductLimit <= 0
    },

    /**
     * Compute max product limit.
     * - Check if product is already in cart.
     * - If no product in cart return default limit.
     * - If product is in cart return remaining limit.
     * @returns {Number}
     */
    maxProductLimit() {
      const currentProduct = this.cart.items?.find((item) => {
        if (!item) {
          return false
        }

        return this.product?.id === item?.product_id
      })

      if (!currentProduct) {
        return this.product?.limits?.perCustomer
      }

      return this.product?.limits?.perCustomer - currentProduct?.quantity
    },

    /**
     * Compute to check if quick add redirect button should be displayed.
     * @returns {Boolean}
     */
    showQuickAddRedirectButton() {
      return this.isProductCard && (
        this.isPersonalisationProduct ||
        this.isBookingProduct ||
        this.showBackInStockButton ||
        this.product?.price.varies ||
        this.personalisedBothSideTag
      )
    },

    /**
     * Is pre-order tag.
     */
    hasPreOrderTag() {
      return this.product.tags?.some((tag) => tag.includes('Pre-Order'))
    },

    /**
     * Is pre-order.
     */
    isPreOrder() {
      return this.hasPreOrderTag && this.isPreOrderAvailable
    },

    /**
     * Is pre-order available.
     */
    isPreOrderAvailable() {
      if (
        handleDate('current-date', this.product?.metafields?.preOrderAvailableDate) ||
        handleDate('past-date', this.product?.metafields?.preOrderAvailableDate)
      ) {
        // When closing passed, add extra days to available date
        if (this.isPreOrderClosing && this.product?.metafields?.preOrderAvailableDate) {
          const extendedDate = new Date(this.product?.metafields?.preOrderAvailableDate)
          if (isNaN(extendedDate.getTime())) {
            return false
          }
          const daysToAdd = parseInt(this.$string('product.pre_order.order_date_plus_variable'))
          if (!isNaN(daysToAdd) && daysToAdd > 0) {
            extendedDate.setDate(extendedDate.getDate() + daysToAdd)
          }
          return handleDate('future-date', extendedDate)
        }
        return false
      }

      return true
    },

    /**
     * Is pre-order closing.
     */
    isPreOrderClosing() {
      return handleDate('current-date', this.product?.metafields?.preOrderClosingDate) ||
        handleDate('past-date', this.product?.metafields?.preOrderClosingDate)
    },

    /**
     * Is pre-order launch soon.
     */
    isPreOrderLaunch() {
      return this.product?.metafields?.preOrderLaunchDate &&
        handleDate('future-time', this.product?.metafields?.preOrderLaunchDate)
    },

    /**
     * Is pre-order available message.
     */
    isPreOrderAvailableMessage() {
      return this.product?.metafields?.preOrderAvailableMessage
    },

    /**
     * Pre-order available message.
     */
    preOrderAvailableMessage() {
      if (!this.product?.metafields?.preOrderAvailableDate) {
        return ''
      }
      if (this.isPreOrderAvailable) {
        // When closing passed, use extended date
        const dateToUse = new Date(this.product?.metafields?.preOrderAvailableDate)
        if (isNaN(dateToUse.getTime())) {
          return ''
        }
        // Add extra days when closing date has passed
        if (this.isPreOrderClosing) {
          const daysToAdd = parseInt(this.$string('product.pre_order.order_date_plus_variable'))
          if (!isNaN(daysToAdd) && daysToAdd > 0) {
            dateToUse.setDate(dateToUse.getDate() + daysToAdd)
          }
        }
        // Subtract days when closing date has NOT passed
        if (this.product?.metafields?.preOrderClosingDate && !this.isPreOrderClosing) {
          const daysToSubtract = parseInt(this.$string('product.pre_order.ship_date_minus_variable')) || 0
          if (!isNaN(daysToSubtract) && daysToSubtract > 0) {
            const calculatedDate = new Date(dateToUse)
            calculatedDate.setDate(calculatedDate.getDate() - daysToSubtract)
            // Only use calculated date if it's not in the past
            if (!handleDate('past-date', calculatedDate)) {
              dateToUse.setTime(calculatedDate.getTime())
            }
          }
        }
        const formatDate = `<b>${this.$formatDate('date_formats.day_singular_full_month_year', dateToUse)}</b>`

        if (this.isPreOrderAvailableMessage) {
          return `${this.isPreOrderAvailableMessage} ${formatDate}.`
        }

        return this.$string(
          'product.pre_order.generic_available_message_date_html',
          {
            replace: {
              date: formatDate,
            },
          },
        )
      }

      return ''
    },

    /**
     * Available to buy date.
     * - Check if 'availableToBuyDate' date is in the future.
     */
    hasAvailableToBuyDate() {
      return this.product?.metafields?.availableToBuyDate &&
        // eslint-disable-next-line max-len
        handleDate('future-time', this.product?.metafields?.availableToBuyDate)
    },
  },

  watch: {

    /**
     * Watch product to reset quantity, error, and line item properties.
     * - Also load recipient form component if gift card.
     */
    product() {
      this.quantity = 1
      this.error = ''
      this.lineItemProperties = {}

      if (!this.product.giftCard) {
        return
      }

      this.loadComponent('recipient-form')
    },

    /**
     * Watch variant to reset error.
     */
    variant() {
      this.error = ''
    },

    /**
     * Watch initialQuantity prop to sync quantity from parent.
     */
    initialQuantity(newVal) {
      if (newVal !== this.quantity) {
        this.quantity = newVal
      }
    },
  },

  mounted() {
    this.checkForBIS()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      loadComponent: 'components/load',
      openOverlay: 'overlays/open',
      setScroll: 'setScroll',
    }),

    /**
     * Check for the `window.BIS` variable to check if that the BIS app is
     * loaded.
     * - Checks every 200ms, and times out after 60s.
     */
    checkForBIS() {
      const checkInterval = 200
      const timeout = 60000
      let elapsedTime = 0

      const intervalId = setInterval(() => {
        if (window.BIS) {
          this.backInStockAppActive = true
          clearInterval(intervalId)
          return
        }

        elapsedTime += checkInterval
        if (elapsedTime >= timeout) {
          clearInterval(intervalId)
        }
      })
    },

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      if (this.product.personalisation?.enabledWithoutInput) {
        this.showErrorMessages = !this.product.personalisation?.enabledWithoutInput
      }

      if (!this.variant || this.showErrorMessages) {
        this.error = this.isPersonalisationProduct
          ? this.$string('product.personalisation.add_personalisation')
          : this.$string('product.form.select_variant')

        return
      }

      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      if (this.$variable('storybook')) {
        if (!this.variant.available) {
          this.error = this.$string('product.form.sold_out')
          this.state = 'ready'
          return
        }

        this.timeout = setTimeout(() => {
          this.state = 'added'
        }, this.$timing('slow'))

        this.timeout = setTimeout(() => {
          this.state = 'ready'
        }, this.$timing('message'))

        await this.resetState()
        return
      }

      if (this.hasPreOrderTag && !this.product?.metafields?.preOrderAvailableDate) {
        this.lineItemProperties._pre_order = this.$string('product.pre_order.generic_default_message_html')

        /**
         * `_pre_order_formatted` is used for email templates
         *  to remove HTML markup '<b>' and '</b>'.
         */
        this.lineItemProperties._pre_order_formatted = this.$string('product.pre_order.generic_default_message_html').replaceAll('<b>', '').replaceAll('</b>', '')

      } else if (this.isPreOrder) {
        this.lineItemProperties._pre_order = this.preOrderAvailableMessage

        /**
         * `_pre_order_formatted` is used for email templates
         *  to remove HTML markup '<b>' and '</b>'.
         */
        this.lineItemProperties._pre_order_formatted = this.preOrderAvailableMessage.replaceAll('<b>', '').replaceAll('</b>', '')
      }

      const items = []

      if (Object.keys(this.lineItemProperties).length === 0) {
        if (this.variant.selectedOptions[0].value === 'Default Title') {
          items.push({
            id: this.variant.id,
            properties: null,
            quantity: this.quantity,
          })
        } else {
          const selectedOptions = this.variant.selectedOptions.reduce((acc, option) => {
            // Exclude "Text" option to prevent Algolia web pixel from
            // interpreting the numeric text position value as quantity.
            if (option.name.toLowerCase() !== 'text') {
              acc[`_${option.name}`] = option.value
            }
            return acc
          }, { _personalised: true })

          if (!this.personalisedWithoutText) {
            delete selectedOptions._personalised
          }

          if (this.personalisedBothSide) {
            const handleValue = this.variant?.selectedOptions[1]?.value
            selectedOptions['Right Handle'] = handleValue
            selectedOptions['Left Handle'] = handleValue
          }

          items.push({
            id: this.variant.id,
            properties: selectedOptions,
            quantity: this.quantity,
          })
        }

      } else {
        const selectedOptions = this.variant.selectedOptions.reduce((acc, option) => {
          if (option.name.toLowerCase() !== 'text') {
            acc[`_${option.name}`] = option.value
          }
          return acc
        }, {})

        const selectedValue = this.variant?.selectedOptions[1]?.value
        if (this.product.personalisation?.enabledWithoutInput &&
          this.lineItemProperties['Right Handle']) {
          this.lineItemProperties['Right Handle'] = selectedValue
        } else if (this.product.personalisation?.enabledWithoutInput &&
          this.lineItemProperties['Left Handle']) {
          this.lineItemProperties['Left Handle'] = selectedValue
        }
        // Merge selected into lineItemProperties
        this.lineItemProperties = {
          ...this.lineItemProperties,
          ...selectedOptions,
        }

        items.push({
          id: this.variant.id,
          properties: this.lineItemProperties,
          quantity: this.quantity,
        })
      }

      if (this.lineItemProperties._personalisationAddon) {
        items.push({
          id: this.lineItemProperties._personalisationAddon,
          quantity: this.quantity,
          properties: {
            _addon: true,
            _groupId: this.lineItemProperties._groupId,
            'Unscripted Admin Fee': 'Unscripted product',
          },
        })
      }

      // Match existing cart item properties so line items merge correctly.
      // This must happen before Algolia context handling.
      items.forEach((item) => {
        const existingItem = this.cart?.items?.find((cartItem) => {
          return cartItem.variant_id === item.id
        })

        if (existingItem) {
          // Copy Algolia properties from the existing item.
          const existingAlgoliaProps = {}
          if (existingItem.properties?._algoliaQueryID) {
            existingAlgoliaProps._algoliaQueryID = existingItem.properties._algoliaQueryID
          }
          if (existingItem.properties?._algoliaIndexName) {
            existingAlgoliaProps._algoliaIndexName = existingItem.properties._algoliaIndexName
          }
          if (existingItem.properties?._algoliaObjectID) {
            existingAlgoliaProps._algoliaObjectID = existingItem.properties._algoliaObjectID
          }

          if (Object.keys(existingAlgoliaProps).length > 0) {
            item.properties = {
              ...item.properties,
              ...existingAlgoliaProps,
            }
          }
        }
      })

      try {
        // Attach Algolia query context for conversion tracking.
        let queryContext = null

        if (this.product?.__queryID) {
          // Product has Algolia context from PLP/search.
          queryContext = {
            __queryID: this.product.__queryID,
            __indexName: this.product.__indexName,
            __position: this.product.__position,
            __objectID: String(this.product.variantId || this.product.objectID),
          }
        } else {
          // Check sessionStorage for context from search or
          // collection navigation to PDP.
          try {
            const contextRaw = sessionStorage.getItem('algolia_query_context')
            if (contextRaw) {
              const context = JSON.parse(contextRaw)
              if (context?.queryID) {
                queryContext = {
                  __queryID: context.queryID,
                  __indexName: context.indexName,
                  __position: context.position,
                  __objectID: context.objectID,
                }
              }
            }
          } catch (error) {
            // Silently fail.
          }
        }

        if (queryContext) {
          items.forEach((item) => {
            // Set on item for Vue cart.js conversion tracking.
            item.__queryID = queryContext.__queryID
            item.__indexName = queryContext.__indexName
            item.__position = queryContext.__position

            // Check if this variant already exists in cart.
            const existingItem = this.cart?.items?.find((cartItem) => {
              return cartItem.variant_id === item.id
            })

            if (!existingItem) {
              // Only pass a real queryID to the pixel.
              const isRealQueryID = typeof queryContext.__queryID === 'string' &&
                !queryContext.__queryID.startsWith('recommendation_')

              item.properties = {
                ...item.properties,
                ...isRealQueryID
                  ? { _algoliaQueryID: queryContext.__queryID }
                  : {},
                _algoliaIndexName: queryContext.__indexName,
                _algoliaObjectID: queryContext.__objectID,
              }
            }
            // If existingItem exists, Algolia props were already copied above.
          })
        }
        await this.addItem(items)
        this.state = 'added'

        if (this.isMobile) {
          this.setScroll({
            direction: 'up',
            header: 'open',
            top: false,
            x: window.scrollX,
            y: window.scrollY,
          })
        }

        if (
          this.localSettings.autoOpenCartDrawer &&
          this.getComponentExists('cart-drawer')
        ) {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          })
        }

        await this.resetState()

      } catch (error) {
        this.state = 'error'

        if (error.description) {
          this.handleErrorMessage(error.description)
          return
        }

        cnvs.ReportError('Failed to add product to cart', error)
      }
    },

    /**
     * Reset product form state after adding to cart after message delay.
     */
    async resetState() {
      if (this.onePerOrderEnabled) {
        await cnvs.Sleep(this.$timing('quick'))
        this.quantity = 1
      }

      await cnvs.Sleep(this.$timing('message'))
      this.state = 'ready'

      /**
       * Reset gift card recipient form based on setting.
       */
      if (
        this.product.giftCard &&
        this.localSettings.resetRecipientFormOnAdd &&
        typeof this.$refs.recipientForm.reset() === 'function'
      ) {
        this.$refs.recipientForm.reset()
      }

      /**
       * Reset variant selection based on setting.
       */
      if (!this.localSettings.resetFormOnAdd) {
        return
      }

      this.handleUpdateVariant(false)
      this.quantity = 1
    },

    /**
     * Handle update variant event.
     * @param {Boolean|Null|Object} variant - Emitted variant.
     */
    handleUpdateVariant(variant) {
      this.$emit('update-variant', variant)
    },

    /**
     * Handle update quantity event.
     * @param {String} quantity - Emitted quantity.
     */
    handleUpdateQuantity(quantity) {
      this.quantity = quantity
      this.$emit('update-quantity', quantity)
    },

    /**
     * Handle update line item properties event.
     * - If property is set to null it overrides existing value and is removed.
     * @param {Object} lineItemProperties - Emitted line item properties.
     */
    handleUpdateLineItemProperties(lineItemProperties) {
      const combinedLineItemProperties = {
        ...this.lineItemProperties,
        ...lineItemProperties,
      }

      Object.entries(combinedLineItemProperties).forEach(([key, value]) => {
        if (value !== null) {
          return
        }

        delete combinedLineItemProperties[key]
      })

      this.lineItemProperties = combinedLineItemProperties
    },

    /**
     * Handle personalisation update.
     * - Updates the variant state and the line item properties.
     * @param {Object} payload - Personalisation Payload.
     * @param {Object|Boolean} payload.variant - Personalisation variant
     * @param {Object} payload.lineItemProperties - Personalisation line item
     * properties.
     */
    handleUpdatePersonalisation(payload) {
      this.showErrorMessages = payload.totalCharacterCount === 0 && payload.personalisation
      this.handleUpdateLineItemProperties(payload.lineItemProperties)
      this.$emit('update-personalising', payload.personalisation)
      if (!this.product.personalisation?.enabledWithoutInput) {
        this.handleUpdateVariant(payload.variant)
      }
    },

    /**
     * Handle error message event.
     * @param {Object} errorDescription - Error description from add request.
     */
    handleErrorMessage(errorDescription) {
      if (typeof errorDescription === 'string') {
        this.error = errorDescription
        return
      }

      this.error = Object.values(errorDescription)
        .flat()
        .map((error) => {
          return error.endsWith('.') ? error : `${error}.`
        })
        .join('<br>')
    },
  },
}
</script>

<style lang="scss">
@import './recipient-form';
@import './product-form';
</style>
